import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useDarkMode } from "../../context/DarkModeContext";
import { useSelector, useDispatch } from "react-redux";
import { showSwal } from "../../Helper/ShowAlert";
import { updateTag as updateTagAction, deleteTag as deleteTagAction } from "../../redux/action";
import { useNavigate } from "react-router-dom";

const TagEditModal = ({ isOpen, onClose, tag, mode = "edit" }) => {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginToken = useSelector((state) => state.reducer.userToken);
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6366F1");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (tag) {
      setName(tag.name || "");
      setColor(tag.color || "#6366F1");
    }
  }, [tag]);

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setColor("#6366F1");
      setErrors({});
    }
  }, [isOpen]);

  // Escape closes the modal.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const validateHexColor = (hex) => {
    return /^#[0-9A-Fa-f]{6}$/.test(hex);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!name || name.trim().length === 0) {
      setErrors({ name: "Tag name is required" });
      return;
    }

    if (name.trim().length > 50) {
      setErrors({ name: "Tag name cannot exceed 50 characters" });
      return;
    }

    if (!validateHexColor(color)) {
      setErrors({ color: "Color must be a valid hex color (#RRGGBB)" });
      return;
    }

    setLoading(true);
    try {
      dispatch(updateTagAction(loginToken, userLoggedIn?.role, tag._id, name.trim(), color.toUpperCase()));
      showSwal("success", "Tag updated successfully!", 200);
      onClose();
    } catch (error) {
      console.error("Error updating tag:", error);
      showSwal("error", "Failed to update tag. Please try again.", 500);
      setErrors({ general: error.message || "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    // No need for browser confirm - the modal itself is the confirmation
    setLoading(true);
    try {
      dispatch(deleteTagAction(loginToken, userLoggedIn?.role, tag._id));
      showSwal("success", "Tag deleted successfully!", 200);

      // If we're on the tag's page, navigate away
      if (window.location.pathname.includes(`/operator/tags/${tag._id}`)) {
        navigate("/operator/incomplete-queries");
      }

      onClose();
    } catch (error) {
      console.error("Error deleting tag:", error);
      showSwal("error", "Failed to delete tag. Please try again.", 500);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !tag) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tag-edit-modal-title"
        className={`w-full max-w-md rounded-xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${isDarkMode
            ? "bg-gray-800/95 border-gray-700/50"
            : "bg-white/95 border-gray-200/50"
          } border`}
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: "Inter, Manrope, Poppins, sans-serif",
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          transform: 'scale(1)',
          animation: 'modalFadeIn 0.2s ease-out',
        }}
      >
        <div
          className={`px-5 py-4 border-b flex items-center justify-between ${isDarkMode
              ? "bg-gray-700/80 border-gray-600/50 backdrop-blur-sm"
              : "bg-gray-50/80 border-gray-200/50 backdrop-blur-sm"
            }`}
          style={{
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <h2
            id="tag-edit-modal-title"
            className={`text-lg font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-800"
              }`}
          >
            {mode === "delete" ? "Delete Tag" : "Edit Tag"}
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 rounded hover:bg-opacity-20 transition-colors duration-150 ${isDarkMode ? "text-gray-300 hover:bg-white" : "text-gray-600 hover:bg-gray-200"
              }`}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {mode === "delete" ? (
          <div className="p-5 space-y-4">
            <p className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
              Are you sure you want to delete the tag <strong>"{tag.name}"</strong>? This action cannot be undone and will remove the tag from all quotes.
            </p>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={`flex-1 px-4 py-2 rounded font-medium transition-colors duration-150 ${isDarkMode
                    ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 rounded font-medium text-white transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: loading ? "#9CA3AF" : "#EF4444",
                }}
              >
                {loading ? "Deleting..." : "Delete Tag"}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-5 space-y-4">
            {errors.general && (
              <div
                className="p-3 rounded text-sm"
                style={{
                  backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.06)",
                  color: "#EF4444",
                }}
              >
                {errors.general}
              </div>
            )}

            <div>
              <label
                className={`block text-sm font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Tag Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors({ ...errors, name: null });
                }}
                maxLength={50}
                className={`w-full px-3 py-2 rounded border transition-colors duration-150 focus:outline-none focus:ring-2 ${isDarkMode
                    ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500"
                  } ${errors.name ? "border-red-500" : ""}`}
                placeholder="e.g., Negotiation, Docs Needed"
                disabled={loading}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {name.length}/50 characters
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => {
                    setColor(e.target.value.toUpperCase());
                    if (errors.color) setErrors({ ...errors, color: null });
                  }}
                  className="w-16 h-10 rounded border cursor-pointer"
                  disabled={loading}
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setColor(value);
                    if (errors.color) setErrors({ ...errors, color: null });
                  }}
                  className={`flex-1 px-3 py-2 rounded border transition-colors duration-150 focus:outline-none focus:ring-2 font-mono text-sm ${isDarkMode
                      ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500"
                      : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500"
                    } ${errors.color ? "border-red-500" : ""}`}
                  placeholder="#6366F1"
                  maxLength={7}
                  disabled={loading}
                />
              </div>
              {errors.color && (
                <p className="mt-1 text-xs text-red-500">{errors.color}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Hex color format: #RRGGBB
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={`flex-1 px-4 py-2 rounded font-medium transition-colors duration-150 ${isDarkMode
                    ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !name.trim()}
                className="flex-1 px-4 py-2 rounded font-medium text-white transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: loading || !name.trim() ? "#9CA3AF" : "#6366F1",
                }}
              >
                {loading ? "Updating..." : "Update Tag"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TagEditModal;

